import React, { useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  CheckCircle2, Clock, Circle, AlertTriangle, Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react';

/**
 * Roadmap & Backlog
 * Static product roadmap reflecting the current state of the DCA Enterprises platform.
 * Items are grouped by priority: P0 (now), P1 (next), P2 (later), Future (backlog).
 * To update, ask the agent - these are stored here as the source of truth for in-app visibility.
 */
const ROADMAP = [
  {
    id: 'p0',
    label: 'P0 - In Progress',
    color: 'amber',
    icon: Clock,
    items: [],
  },
  {
    id: 'p1',
    label: 'P1 - Awaiting Your Action',
    color: 'sky',
    icon: AlertTriangle,
    items: [
      {
        title: 'Re-add the "Coaches" tab to the header when coaches are onboarded',
        body: 'The Coaches link was removed from the main navigation (no active coaches yet). The full coach platform is intact and live — public pages /coaches, /coaches/apply, /coaches/:slug, the Coach Dashboard (/coach/dashboard) with Payouts, and the homepage "Meet Our Business Coaches" section (auto-appears once an active coach exists). When you approve your first coach, just ask the agent to put the "Coaches" link back in the header.',
      },
      {
        title: 'Add Zoom orphan-deletion scope',
        body: 'Add the meeting:delete:meeting:admin scope to your Zoom S2S OAuth App. Once enabled, cancelled/rescheduled bookings will automatically remove the Zoom meeting too.',
      },
      {
        title: 'Add Twilio API keys (SMS reminders)',
        body: 'Currently MOCKED. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER to backend env to activate live SMS reminders 24h and 1h before booked sessions.',
      },
      {
        title: 'Add PayPal Client ID + Secret',
        body: 'Currently disabled. Add PAYPAL_CLIENT_ID and PAYPAL_SECRET to enable a 2nd checkout option alongside Stripe.',
      },
      {
        title: 'Upload real brand logos in /admin/booking-brands',
        body: 'Each of the 5 booking brands (DCA, SEG Career, CareerSupport.io, Purpose Fit, Purpose Transformation) currently displays the brand name only. Upload logos via /admin/booking-brands → Edit → Logo URL.',
      },
      {
        title: 'Paste iframe snippets into external brand sites',
        body: 'After you deploy DCA Enterprises to its production domain, copy each brand iframe from /admin/links and paste into the corresponding external site (SEG Career Support, CareerSupport.io, Purpose Fit Life, Purpose Transformation).',
      },
    ],
  },
  {
    id: 'p2',
    label: 'P2 - Tech Debt',
    color: 'slate',
    icon: Circle,
    items: [
      {
        title: 'Refactor server.py (5000+ lines) into routes/',
        body: 'Split monolithic backend into /app/backend/routes/ modules (auth.py, scheduling.py, brands.py, programs.py, admin.py, payments.py). Improves maintainability as features grow.',
      },
      {
        title: 'Migrate other admin pages to shared AdminLayout',
        body: 'AdminLinks, AdminRoadmap, and AdminBookingBrands use the new shared <AdminLayout> component. Other admin pages still have their inline navs. Migrate them for consistency.',
      },
      {
        title: 'Extract shared _persist_booking() helper',
        body: '/api/book-slot and /api/brand-book-slot duplicate ~80 lines of booking persistence logic (Zoom creation, ICS attachment, notification, lead auto-create). Extract into a shared helper to avoid drift.',
      },
      {
        title: 'Tighten iframe CSP frame-ancestors in production',
        body: 'Currently any origin can embed the /embed/schedule pages. In production, restrict to the explicit list of 5 brand domains via a CSP middleware.',
      },
      {
        title: 'Track external site referrer on bookings',
        body: 'brand_slug is captured on every booking, but the actual referring URL (e.g. https://segcareersupport.com/coaching/) is not. Adding it improves analytics and attribution.',
      },
    ],
  },
  {
    id: 'future',
    label: 'Future / Backlog',
    color: 'indigo',
    icon: Sparkles,
    items: [
      {
        title: 'Client Settings page (/portal/settings)',
        body: 'Add profile editing (name, avatar), password change, notification preferences, and account deletion for paying clients. Currently the portal is read-only.',
      },
      {
        title: 'New-user onboarding nudge',
        body: 'After client signup, show a sticky banner / modal "Book your FREE 20-min Jumpstart now →" to drive new users to first-value action within 2 minutes.',
      },
      {
        title: 'Admin User Management with role mgmt',
        body: 'A /admin/users page (separate from /admin/clients) for full RBAC: search/filter all accounts, promote to admin / demote, suspend/activate, view login history.',
      },
      {
        title: 'Admin Activity Log',
        body: '/admin/activity - historical feed of every event: signups, logins, bookings, payments, applications, admin actions. Filterable by user / date / event type.',
      },
      {
        title: 'Homepage "How It Works" 3-step section',
        body: 'Add a dedicated Book → Strategize → Launch section to the homepage. Currently implied across pages but not consolidated.',
      },
      {
        title: 'Homepage trust metric strip',
        body: 'Below the hero: "11 brands launched · X entrepreneurs coached · Y free sessions delivered · 5 coaching specialties". Drives credibility above the fold.',
      },
      {
        title: 'Scroll-reveal animations on landing',
        body: 'Subtle fade/slide-in on hero, feature cards, and testimonials as user scrolls. Adds polish without changing visuals. Motion library or intersection observer.',
      },
      {
        title: 'Route alias /dashboard → /portal',
        body: 'For users with muscle memory typing /dashboard, redirect to /portal (the client dashboard).',
      },
      {
        title: 'WordPress plugin for [dca-scheduler] shortcode',
        body: 'A small WP plugin (1 file, ~80 lines) so WordPress brand sites can drop in [dca-scheduler brand="segcareer"] as a shortcode instead of raw iframe HTML. DEFERRED: most WP sites are being rebuilt on Emergent, so this is low priority.',
      },
      {
        title: 'Per-brand analytics dashboard',
        body: 'Mini dashboard per booking brand: "Purpose Fit Life booked 14 calls this month, $1,778 revenue, 3 leads in pipeline." Filter the existing /admin/bookings by brand.',
      },
      {
        title: 'Admin testimonials CRUD page',
        body: 'Currently testimonials are seeded once in the DB and updated only via agent. A /admin/testimonials page would let David add/edit/feature/toggle testimonials live without coding.',
      },
      {
        title: '/admin/program-applications dedicated UI',
        body: 'Program applications are currently visible via the /admin/leads mirror. A dedicated page would surface the 13-field applications with their full context.',
      },
      {
        title: 'Consolidate program metadata source',
        body: 'Program details (name, price, duration, blurb) are duplicated across ProgramLandingPage, ProgramsPage, and ProgramApplicationPage. Pull from a single backend source.',
      },
      {
        title: 'Brand-specific Stripe checkout per service',
        body: 'Right now only DCA $197 Strategy Session has a wired Stripe checkout. The paid services on the 4 non-DCA brands ($147 / $127) currently book via /brand-book-slot but do not collect payment. Add per-brand Stripe checkout flow.',
      },
      {
        title: 'Multi-brand confirmation email customization',
        body: 'Each brand has a default branded HTML email template. A future enhancement: let the admin write custom email subject/body per brand from the booking-brands edit modal.',
      },
    ],
  },
  {
    id: 'completed',
    label: '✓ Completed Recently',
    color: 'emerald',
    icon: CheckCircle2,
    items: [
      { title: 'Admin Booking Calendar (/admin/calendar)', body: 'Month-grid visible calendar showing every booking with name + time per day. Click any booking for full details + intake responses + Zoom link.' },
      { title: 'Per-link conversion overlay', body: 'On /admin/links each row shows: views, 7-day views, conversions (bookings/applications/leads) and conversion rate %.' },
      { title: 'Production URL setting (/admin/links → top)', body: 'Set a custom production URL; all generated links and iframe snippets automatically use it instead of the preview origin.' },
      { title: 'Multi-brand iframe scheduler (Phase 15)', body: '5 booking brands seeded, /coaching hub, /embed/schedule themed scheduler, /admin/booking-brands CRUD, cross-brand availability.' },
      { title: 'Link Manager (this page\'s sibling) - /admin/links', body: 'Centralized place to find every URL on the site.' },
      { title: 'Testimonials carousel: 3 → 8 with auto-rotation', body: 'Used on / and /programs.' },
      { title: 'Brand portfolio expansion + category overhaul', body: '11 brands with tightened categories (Digital Universe, Insurance Products, Career Development, Life & Purpose Coaching, Fresh Local Produce, Health & Wellness).' },
      { title: 'Programs Funnel v2 - 5 dedicated landing pages', body: 'Each program has its own URL: /free-jumpstart-session, /business-strategy-session, /business-jumpstart-accelerator, /business-growth-partnership, /visionary-mentorship.' },
      { title: 'Program application form + backend', body: 'Dedicated /program-application page with 13-field intake → email + DB.' },
    ],
  },
];

const COLOR_MAP = {
  amber: { ring: 'border-amber-300', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  sky: { ring: 'border-sky-300', dot: 'bg-sky-500', text: 'text-sky-700', bg: 'bg-sky-50' },
  slate: { ring: 'border-slate-300', dot: 'bg-slate-500', text: 'text-slate-700', bg: 'bg-slate-50' },
  indigo: { ring: 'border-indigo-300', dot: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50' },
  emerald: { ring: 'border-emerald-300', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
};

export default function AdminRoadmap() {
  const [expanded, setExpanded] = useState({ p1: true, future: true });
  const toggle = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  return (
    <AdminLayout title="Roadmap & Backlog">
      <div className="max-w-4xl mx-auto" data-testid="admin-roadmap-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]">Roadmap &amp; Backlog</h1>
          <p className="text-sm text-[#475569] mt-1">Track what is in progress, what needs your input, and what is being deferred.</p>
        </div>

        <div className="space-y-4">
          {ROADMAP.map((section) => {
            const c = COLOR_MAP[section.color];
            const Icon = section.icon;
            const isOpen = expanded[section.id] ?? false;
            const Chevron = isOpen ? ChevronUp : ChevronDown;
            return (
              <div key={section.id} className={`bg-white border ${c.ring} rounded-xl overflow-hidden`} data-testid={`roadmap-section-${section.id}`}>
                <button
                  onClick={() => toggle(section.id)}
                  className={`w-full flex items-center justify-between px-5 py-4 ${c.bg} hover:opacity-90`}
                  data-testid={`roadmap-toggle-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${c.dot} text-white flex items-center justify-center`}>
                      <Icon size={16} />
                    </div>
                    <div className="text-left">
                      <div className={`font-bold ${c.text} text-base`}>{section.label}</div>
                      <div className="text-xs text-[#475569]">{section.items.length} item{section.items.length === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                  <Chevron size={18} className="text-[#475569]" />
                </button>
                {isOpen && (
                  <div className="divide-y divide-[#F1F5F9]">
                    {section.items.length === 0 ? (
                      <div className="px-5 py-5 text-sm text-[#94A3B8] text-center">Nothing in this lane right now.</div>
                    ) : section.items.map((item, i) => (
                      <div key={i} className="px-5 py-4 hover:bg-[#FAFAFA]" data-testid={`roadmap-item-${section.id}-${i}`}>
                        <div className="font-bold text-sm text-[#0A0F1C] mb-1">{item.title}</div>
                        <div className="text-xs text-[#475569] leading-relaxed">{item.body}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 bg-white border border-dashed border-[#E2E8F0] rounded-xl px-5 py-4 text-xs text-[#475569] text-center" data-testid="roadmap-footer">
          To add, update, or reprioritize a roadmap item, just ask the agent.
        </div>
      </div>
    </AdminLayout>
  );
}
